import { useEffect, useRef } from "react";
import { GridFilterModel, GridSortModel } from "@mui/x-data-grid-premium";
import { DataGridUrlStateManager } from "../DataGridUrlStateManager";

interface UseGridUrlSyncOptions {
  apiRef: React.RefObject<any>;
  urlStateManager: DataGridUrlStateManager;
}

/**
 * Hook for synchronizing grid state (filter, sort, column visibility) with URL.
 * Also handles auto-sizing columns on visibility changes.
 */
export function useGridUrlSync({
  apiRef,
  urlStateManager,
}: UseGridUrlSyncOptions): void {
  const autosizeDebounceRef = useRef<NodeJS.Timeout | null>(null);

  // Sync filter model changes to URL
  useEffect(() => {
    if (!apiRef.current) return;

    const handleFilterModelChange = (newFilterModel: GridFilterModel) => {
      urlStateManager.writeFilterModelToUrl(newFilterModel);
    };

    const unsubscribe = apiRef.current.subscribeEvent(
      "filterModelChange",
      handleFilterModelChange
    );

    return () => {
      unsubscribe();
    };
  }, [apiRef, urlStateManager]);

  // Sync sort model changes to URL
  useEffect(() => {
    if (!apiRef.current) return;

    const handleSortModelChange = (newSortModel: GridSortModel) => {
      urlStateManager.writeSortModelToUrl(newSortModel);
    };

    const unsubscribe = apiRef.current.subscribeEvent(
      "sortModelChange",
      handleSortModelChange
    );

    return () => {
      unsubscribe();
    };
  }, [apiRef, urlStateManager]);

  // Auto-size columns when column visibility changes
  useEffect(() => {
    if (!apiRef.current) return;

    const handleAutosize = () => {
      // Clear any existing debounce timeout
      if (autosizeDebounceRef.current) {
        clearTimeout(autosizeDebounceRef.current);
      }

      // Debounce the autosize call to prevent multiple rapid executions
      const timeout = setTimeout(() => {
        if (apiRef.current) {
          const columnsToAutosize = apiRef.current
            .getAllColumns()
            .filter((col: any) => !col.disableAutosize)
            .map((col: any) => col.field);
          apiRef.current.autosizeColumns({
            includeOutliers: true,
            columns: columnsToAutosize,
          });
        }
      }, 150);

      autosizeDebounceRef.current = timeout;
    };

    // Only subscribe to columnVisibilityModelChange to avoid resize on row selection
    const unsubscribeVisibilityChange = apiRef.current.subscribeEvent(
      "columnVisibilityModelChange",
      handleAutosize
    );

    // Cleanup subscription on unmount
    return () => {
      unsubscribeVisibilityChange();
      if (autosizeDebounceRef.current) {
        clearTimeout(autosizeDebounceRef.current);
      }
    };
  }, [apiRef]);
}
